import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart2, RefreshCw, ChevronUp, ChevronDown, ChevronsUpDown,
  Loader2, Search, ChevronLeft, ChevronRight, Clock, Activity, Eye,
  X, Copy, CheckCheck, Printer, Download,
} from 'lucide-react';
import axios from 'axios';
import { interspireApi } from '../services/api';
import { EmailSendRow } from '../types';
import DomainInvestigator from '../components/DomainInvestigator';
import { GUEST_TOKEN_KEY, useAuth } from '../context/AuthContext';

interface HealthCheckRow { countyState: string; count: number; }
interface HealthCheckResult { segmentName: string; listId: number; breakdown: HealthCheckRow[]; total: number; }

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

const PAGE_SIZE = 20;

// Module-level session cache — survives route changes within the same browser session
let sessionCache: { rows: EmailSendRow[]; fetchedAt: number; cacheTtlMins: number } | null = null;

type SortKey = 'campaignName' | 'listName' | 'segmentName' | 'startDate' | 'recipientsCount';
type SortDir = 'asc' | 'desc';

function formatUnixDate(ts: number | null): string {
  if (ts == null || ts === 0) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown className="h-3.5 w-3.5 ml-1 text-gray-400 inline" />;
  return sortDir === 'asc'
    ? <ChevronUp className="h-3.5 w-3.5 ml-1 text-blue-500 inline" />
    : <ChevronDown className="h-3.5 w-3.5 ml-1 text-blue-500 inline" />;
}

export default function Sends() {
  const { canAccess } = useAuth();
  const [rows, setRows]             = useState<EmailSendRow[]>(sessionCache?.rows ?? []);
  const [loading, setLoading]       = useState(!sessionCache);
  const [error, setError]           = useState<string | null>(null);
  const [fetchedAt, setFetchedAt]   = useState<number | null>(sessionCache?.fetchedAt ?? null);
  const [cacheTtlMins, setCacheTtlMins] = useState<number>(sessionCache?.cacheTtlMins ?? 60);

  // segmentId → fetched count
  const [counts, setCounts]             = useState<Map<number, number>>(new Map());
  const [countLoading, setCountLoading] = useState<Set<number>>(new Set());

  const [sortKey, setSortKey] = useState<SortKey>('startDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const handleSearch = (val: string) => { setSearch(val); setPage(1); };

  // If rendering inside GuestView, carry the token to new-tab pages via ?gt=
  const guestToken = useMemo(() => sessionStorage.getItem(GUEST_TOKEN_KEY), []);
  const gt = guestToken ? `?gt=${encodeURIComponent(guestToken)}` : '';

  // Recent recipients export
  const [exportDays, setExportDays]   = useState(21);
  const [exporting, setExporting]     = useState(false);

  const handleExportRecipients = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `/api/interspire/sends/export/csv?days=${exportDays}`,
        { responseType: 'blob', timeout: 0 }  // no timeout — large exports can take time
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recipients_last_${exportDays}_days.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      window.alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Domain investigator
  const [investigateRow, setInvestigateRow] = useState<EmailSendRow | null>(null);

  // Segment Health Check modal
  const [healthRow, setHealthRow]         = useState<EmailSendRow | null>(null);
  const [healthData, setHealthData]       = useState<HealthCheckResult | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthCopied, setHealthCopied]   = useState(false);

  const openHealthCheck = useCallback(async (row: EmailSendRow) => {
    if (row.segmentId == null) return;
    setHealthRow(row);
    setHealthData(null);
    setHealthLoading(true);
    try {
      const res = await axios.get(`/api/interspire/segments/${row.segmentId}/health`);
      setHealthData(res.data);
    } catch {
      setHealthRow(null);
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const closeHealthCheck = () => { setHealthRow(null); setHealthData(null); setHealthCopied(false); };

  const copyHealthCheck = () => {
    if (!healthData) return;
    const lines = [
      `Segment Health Check: ${healthData.segmentName}`,
      `List ID: ${healthData.listId}`,
      '',
      'County, State\tActive Contacts',
      ...healthData.breakdown.map(r => `${r.countyState}\t${r.count.toLocaleString()}`),
      '',
      `TOTAL\t${healthData.total.toLocaleString()}`,
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    setHealthCopied(true);
    setTimeout(() => setHealthCopied(false), 2500);
  };

  const printHealthCheck = () => {
    if (!healthData) return;
    const tbodyRows = healthData.breakdown
      .map(r => `<tr><td style="padding:3px 14px;border-bottom:1px solid #e5e7eb">${escapeHtml(r.countyState)}</td><td style="padding:3px 14px;text-align:right;border-bottom:1px solid #e5e7eb;font-weight:500">${r.count.toLocaleString()}</td></tr>`)
      .join('');
    const loc = healthData.breakdown.length;
    const html = `<!DOCTYPE html><html><head><title>Segment Health Check – ${healthData.segmentName}</title>
<style>
  body{font-family:Arial,sans-serif;margin:40px;color:#111}
  h1{font-size:18px;margin:0 0 4px}
  .meta{font-size:13px;color:#555;margin:0 0 20px}
  table{width:100%;border-collapse:collapse;font-size:14px}
  thead th{background:#f3f4f6;padding:7px 14px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#6b7280;border-bottom:2px solid #d1d5db}
  thead th:last-child{text-align:right}
  tfoot td{padding:8px 14px;font-weight:700;border-top:2px solid #374151;background:#f3f4f6;font-size:14px}
  tfoot td:last-child{text-align:right}
</style></head>
<body>
  <h1>Segment Health Check</h1>
  <p class="meta">${escapeHtml(healthData.segmentName)}<br>List ID: ${healthData.listId} &nbsp;·&nbsp; ${loc} location${loc !== 1 ? 's' : ''}</p>
  <table>
    <thead><tr><th>County, State</th><th style="text-align:right">Active Contacts</th></tr></thead>
    <tbody>${tbodyRows}</tbody>
    <tfoot><tr><td>TOTAL</td><td>${healthData.total.toLocaleString()}</td></tr></tfoot>
  </table>
</body></html>`;
    const w = window.open('', '_blank', 'width=720,height=900');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  };

  const loadHistory = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await interspireApi.getSendHistory(forceRefresh);
      const { rows: newRows, cacheTtlMins: ttl } = res.data;
      const now = Date.now();
      sessionCache = { rows: newRows, fetchedAt: now, cacheTtlMins: ttl };
      setRows(newRows);
      setFetchedAt(now);
      setCacheTtlMins(ttl);
      setPage(1);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load send history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionCache) loadHistory();
  }, [loadHistory]);

  const fetchCount = useCallback(async (segmentId: number) => {
    if (countLoading.has(segmentId)) return;
    setCountLoading(prev => new Set(prev).add(segmentId));
    try {
      const res = await interspireApi.getSegmentSize(segmentId);
      setCounts(prev => new Map(prev).set(segmentId, res.data.count));
    } catch {
      setCounts(prev => new Map(prev).set(segmentId, -1));
    } finally {
      setCountLoading(prev => { const s = new Set(prev); s.delete(segmentId); return s; });
    }
  }, [countLoading]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const filtered = search.trim()
    ? rows.filter(r => r.campaignName?.toLowerCase().includes(search.trim().toLowerCase()))
    : rows;

  const sorted = [...filtered].sort((a, b) => {
    let av: any, bv: any;
    if (sortKey === 'startDate') { av = a.startDate ?? 0; bv = b.startDate ?? 0; }
    else if (sortKey === 'recipientsCount') { av = a.recipientsCount; bv = b.recipientsCount; }
    else { av = (a[sortKey] ?? '').toString().toLowerCase(); bv = (b[sortKey] ?? '').toString().toLowerCase(); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const thClass = 'px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider select-none cursor-pointer whitespace-nowrap hover:text-gray-700';
  const tdClass = 'px-4 py-3 text-sm text-gray-700';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart2 className="h-6 w-6 text-blue-600" />
            Email Sends
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Campaign send history with segment details from Interspire
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search campaign name…"
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
          />

          {/* Recent recipients CSV export — gated by feature flag */}
          {canAccess('sendsExportRecipients') && <div className="flex items-center gap-1.5 pl-2 border-l border-gray-200 dark:border-slate-600">
            <span className="text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap">Export last</span>
            <input
              type="number"
              min={1}
              max={365}
              value={exportDays}
              onChange={e => setExportDays(Math.max(1, Math.min(365, Number(e.target.value) || 21)))}
              className="w-14 px-2 py-1.5 text-sm text-center rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap">days</span>
            <button
              onClick={handleExportRecipients}
              disabled={exporting || loading}
              title="Export unique recipient emails from all sends in this date range"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {exporting
                ? <RefreshCw className="h-4 w-4 animate-spin" />
                : <Download className="h-4 w-4" />}
              Recipients CSV
            </button>
          </div>}

          <div className="flex flex-col items-end gap-0.5">
            <button
              onClick={() => loadHistory(true)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {fetchedAt && (
              <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-slate-500">
                <Clock className="h-3 w-3" />
                {(() => {
                  const mins = Math.floor((Date.now() - fetchedAt) / 60000);
                  if (mins < 1) return 'just loaded';
                  if (mins === 1) return '1 min ago';
                  return `${mins} min ago`;
                })()}
                {' · '}cache {cacheTtlMins}m
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading send history…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <BarChart2 className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">{rows.length === 0 ? 'No send history found in Interspire' : `No campaigns match "${search}"`}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className={thClass} onClick={() => handleSort('campaignName')}>
                    Campaign Name <SortIcon col="campaignName" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className={thClass} onClick={() => handleSort('listName')}>
                    List Name <SortIcon col="listName" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className={thClass} onClick={() => handleSort('segmentName')}>
                    Segment Name <SortIcon col="segmentName" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className={thClass} onClick={() => handleSort('startDate')}>
                    Start Date <SortIcon col="startDate" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className={thClass} onClick={() => handleSort('recipientsCount')}>
                    Recipients <SortIcon col="recipientsCount" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Segment Count
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginated.map(row => {
                  const sid = row.segmentId;
                  const isLoadingCount = sid != null && countLoading.has(sid);
                  const fetchedCount   = sid != null ? counts.get(sid) : undefined;

                  return (
                    <tr key={row.statid} className="hover:bg-gray-50 transition-colors">
                      <td className={`${tdClass} font-medium text-gray-900 max-w-xs`}>
                        <span className="truncate block" title={row.campaignName}>
                          {row.campaignName || '—'}
                        </span>
                      </td>
                      <td className={`${tdClass} text-gray-600`}>
                        {row.listName || <span className="text-gray-300">—</span>}
                      </td>
                      <td className={tdClass}>
                        {row.segmentName ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            {row.segmentName}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className={`${tdClass} text-gray-500 whitespace-nowrap`}>
                        {formatUnixDate(row.startDate)}
                      </td>
                      <td className={`${tdClass} text-right font-mono`}>
                        {row.recipientsCount.toLocaleString()}
                      </td>
                      <td className={tdClass}>
                        {sid == null ? (
                          <span className="text-gray-300 text-xs">—</span>
                        ) : isLoadingCount ? (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-500">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Fetching…
                          </span>
                        ) : fetchedCount !== undefined ? (
                          <span className={`font-mono text-sm font-semibold ${fetchedCount === -1 ? 'text-red-400' : 'text-green-700'}`}>
                            {fetchedCount === -1 ? 'Error' : fetchedCount.toLocaleString()}
                          </span>
                        ) : (
                          <button
                            onClick={() => fetchCount(sid)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                          >
                            <BarChart2 className="h-3 w-3" />
                            Get Count
                          </button>
                        )}
                      </td>
                      <td className={tdClass}>
                        <div className="flex items-center gap-1.5">
                          <a
                            href={`/sends/${row.statid}${gt}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center p-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                            title="View send statistics"
                          >
                            <BarChart2 className="h-3.5 w-3.5" />
                          </a>
                          <a
                            href={`/sends/${row.statid}/preview${gt}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center p-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                            title="Preview email HTML"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </a>
                          {row.segmentId != null && (
                            <button
                              onClick={() => openHealthCheck(row)}
                              className="inline-flex items-center justify-center p-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                              title="Segment health check"
                            >
                              <Activity className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => setInvestigateRow(row)}
                            className="inline-flex items-center justify-center p-1.5 bg-slate-500 text-white rounded-md hover:bg-indigo-600 transition-colors"
                            title="Investigate domain"
                          >
                            <Search className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Footer: count + pagination */}
            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-4">
              <span className="text-xs text-gray-400">
                {rows.length} send{rows.length !== 1 ? 's' : ''} total
                {counts.size > 0 && ` · ${counts.size} count${counts.size !== 1 ? 's' : ''} loaded`}
              </span>

              {totalPages > 1 && (
                <div className="flex items-center gap-1.5 text-sm">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1 rounded text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-gray-600 tabular-nums">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1 rounded text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Domain Investigator drawer */}
      {investigateRow && (
        <DomainInvestigator
          row={investigateRow}
          onClose={() => setInvestigateRow(null)}
        />
      )}

      {/* Segment Health Check modal */}
      {healthRow && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-0 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white dark:bg-slate-800 dark:border-slate-600">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-slate-600">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-emerald-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Segment Health Check</h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    {healthRow.segmentName ?? `Segment ID ${healthRow.segmentId}`}
                    {healthRow.campaignName && (
                      <span className="ml-2 text-gray-400 dark:text-slate-500">· {healthRow.campaignName}</span>
                    )}
                  </p>
                </div>
              </div>
              <button onClick={closeHealthCheck} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-3">
              {healthLoading ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-slate-400">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3 text-emerald-600" />
                  <p className="text-sm">Counting active subscribers by location…</p>
                  <p className="text-xs text-gray-400 mt-1">This may take a moment for large lists</p>
                </div>
              ) : healthData ? (
                <>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                    List ID: <span className="font-medium text-gray-700 dark:text-gray-200">{healthData.listId}</span>
                    &nbsp;·&nbsp;
                    {healthData.breakdown.length} location{healthData.breakdown.length !== 1 ? 's' : ''}
                  </p>

                  <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-600 max-h-[55vh] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600 text-sm">
                      <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider text-xs">County, State</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider text-xs">Active Contacts</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-slate-700 bg-white dark:bg-slate-800">
                        {healthData.breakdown.map((r, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                            <td className="px-4 py-1.5 text-gray-800 dark:text-gray-200">{r.countyState}</td>
                            <td className="px-4 py-1.5 text-right font-medium text-gray-900 dark:text-white">{r.count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 dark:bg-slate-700 border-t-2 border-gray-300 dark:border-slate-500">
                          <td className="px-4 py-2 font-bold text-gray-900 dark:text-white">TOTAL</td>
                          <td className="px-4 py-2 text-right font-bold text-gray-900 dark:text-white">{healthData.total.toLocaleString()}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              ) : null}
            </div>

            {/* Footer */}
            {healthData && !healthLoading && (
              <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-gray-200 dark:border-slate-600">
                <button
                  onClick={copyHealthCheck}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-500 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                >
                  {healthCopied
                    ? <><CheckCheck className="h-4 w-4 mr-2 text-green-600" />Copied!</>
                    : <><Copy className="h-4 w-4 mr-2" />Copy</>
                  }
                </button>
                <button
                  onClick={printHealthCheck}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print / Save PDF
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
